import { Classes, PanelStack2 } from '@blueprintjs/core';
import * as React from 'react';
import { Dialog } from 'vet-bones/components';

import { refreshDatabases } from 'src/actions/databases/action-creators/refreshDatabases';
import { TEST_IDS } from 'src/constants/testIds';
import {
  generateModelNamedGraph,
  getDefaultProjectModelPrefix,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import {
  AddModelTypeContext,
  useAddModelTypeValue,
} from 'src/containers/ProjectModelDialog/hooks/useAddModelTypeValue';
import {
  ImportModelContext,
  useImportModelValue,
} from 'src/containers/ProjectModelDialog/hooks/useImportModelValue';
import {
  LocalModelContext,
  useLocalModelValue,
} from 'src/containers/ProjectModelDialog/hooks/useLocalModelValue';
import {
  ModelPanelStackContext,
  useModelPanelStack,
  useModelPanelStackValue,
} from 'src/containers/ProjectModelDialog/hooks/useModelPanelStackValue';
import { useResetModelPanels } from 'src/containers/ProjectModelDialog/hooks/useResetModelPanels';
import {
  UploadModelContext,
  useUploadModelValue,
} from 'src/containers/ProjectModelDialog/hooks/useUploadModelValue';
import { ProjectModelDialogFooter } from 'src/containers/ProjectModelDialog/ProjectModelDialogFooter';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { projectSelector } from 'src/selectors/project';
import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const ProjectModelDialogBody: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isConnected = useReduxSelector(isConnectedSelector);
  const { stack, onClose, onOpen } = useModelPanelStack();

  const defaultPortalConnection = useDefaultPortalConnection();
  const resetModelPanels = useResetModelPanels();
  const didConnection = React.useRef(false);

  const handleClose = React.useCallback(() => {
    onClose();
    resetModelPanels();
  }, [onClose, resetModelPanels]);

  const handleRefreshDatabases = React.useCallback(
    () => dispatch(refreshDatabases()),
    [dispatch]
  );

  React.useEffect(() => {
    if (isConnected) {
      handleRefreshDatabases();
    }
  }, [handleRefreshDatabases, isConnected]);

  React.useEffect(() => {
    if (didConnection.current || !defaultPortalConnection) {
      return;
    }
    didConnection.current = true;

    dispatch(setupPortalConnection(defaultPortalConnection));
  }, [defaultPortalConnection, didConnection, dispatch]);

  return (
    <>
      <div
        className={Classes.DIALOG_BODY}
        data-testid={TEST_IDS.projectModelDialog.body}
      >
        <PanelStack2
          onClose={handleClose}
          onOpen={onOpen}
          renderActivePanelOnly
          showPanelHeader={false}
          stack={stack}
        />
      </div>
      <ProjectModelDialogFooter />
    </>
  );
};

export const ProjectModelDialog: React.VFC = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const project = useReduxSelector(projectSelector(projectId));
  const isOpen = useReduxSelector((state) => state.ui.isProjectModelDialogOpen);
  const isPendingAddModelToDb = useReduxSelector(
    (state) => state.databases.pendingAddModelToDatabase
  );

  const modelPanelValue = useModelPanelStackValue();
  const addModelTypeValue = useAddModelTypeValue();
  const importModelValue = useImportModelValue();
  const localModelValue = useLocalModelValue();
  const uploadModelValue = useUploadModelValue();

  const { onReset } = modelPanelValue;
  const { setAddModelType } = addModelTypeValue;
  const {
    isModelDataLoading: isImportModelDataLoading,
    setDatabaseId,
    setSelectedModels,
    setPendingModelData,
    setIsModelDataLoading,
  } = importModelValue;
  const { setIsNameEdited, setIsNamespaceEdited, setName, setNamespace } =
    localModelValue;
  const {
    isModelDataLoading: isUploadModelDataLoading,
    setDatabaseId: setUploadDatabaseId,
    setFile,
    setIsNameEdited: setIsUploadNameEdited,
    setIsNamedGraphIriEdited,
    setName: setUploadName,
    setNamedGraphIri,
    setPendingModelData: setUploadPendingModelData,
  } = uploadModelValue;

  const handleClose = React.useCallback(() => {
    const defaultNamespace = getDefaultProjectModelPrefix({
      name: project?.name || '',
    });
    onReset();
    setAddModelType(null);

    // import
    setDatabaseId('');
    setSelectedModels([]);
    setPendingModelData({});
    setIsModelDataLoading(false);

    // local
    setName(copy.components.projectModelDialog.localModelPanel.nameExampleText);
    setNamespace(defaultNamespace);
    setIsNameEdited(false);
    setIsNamespaceEdited(false);

    // upload
    setUploadDatabaseId('');
    setUploadName(
      copy.components.projectModelDialog.localModelPanel.nameExampleText
    );
    setNamedGraphIri(
      project ? generateModelNamedGraph(project, { name: 'example' }) : ''
    );
    setUploadPendingModelData(null);
    setFile(null);
    setIsUploadNameEdited(false);
    setIsNamedGraphIriEdited(false);

    dispatch(uiSlice.actions.toggledProjectModelDialog({ isOpen: false }));
  }, [
    project,
    onReset,
    setAddModelType,
    setDatabaseId,
    setFile,
    setIsModelDataLoading,
    setIsNameEdited,
    setIsNamespaceEdited,
    setIsUploadNameEdited,
    setIsNamedGraphIriEdited,
    setName,
    setNamespace,
    setNamedGraphIri,
    setPendingModelData,
    setSelectedModels,
    setUploadDatabaseId,
    setUploadName,
    setUploadPendingModelData,
    dispatch,
  ]);

  const isCloseDisabled =
    isPendingAddModelToDb ||
    isImportModelDataLoading ||
    isUploadModelDataLoading;

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-dialog-project-model"
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      isCloseButtonShown={!isCloseDisabled}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      title={copy.components.projectModelDialog.title}
    >
      <ModelPanelStackContext.Provider value={modelPanelValue}>
        <AddModelTypeContext.Provider value={addModelTypeValue}>
          <ImportModelContext.Provider value={importModelValue}>
            <LocalModelContext.Provider value={localModelValue}>
              <UploadModelContext.Provider value={uploadModelValue}>
                <ProjectModelDialogBody />
              </UploadModelContext.Provider>
            </LocalModelContext.Provider>
          </ImportModelContext.Provider>
        </AddModelTypeContext.Provider>
      </ModelPanelStackContext.Provider>
    </Dialog>
  );
};
